import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type ApiScanVideoActiveWorkItem,
  listActiveScanVideoAnalysisOperationsForScan,
  pickLatestScanVideoAnalysisOperationPerVideo,
  readRememberedScanVideoOperationIds,
  readVideoIdFromScanVideoAnalysisInput,
  SCAN_VIDEO_ACTIVE_WORK_POLL_INTERVAL_MS,
  toApiScanVideoActiveWorkItemFromOperation,
} from '@kaiizen/kai-pro-api';

import { useAuth, useKaiProApiClient } from '@/Client/State';

/** Active-work row plus the Operation id used for progress polling after rehydration. */
export type AIScanVideoActiveWorkSnapshotItem = ApiScanVideoActiveWorkItem & {
  operationId?: string;
};

/** Snapshot from {@link useAIScanVideoActiveWork} — pass into the modal to avoid a second poller. */
export type AIScanVideoActiveWorkSnapshot = {
  items: readonly AIScanVideoActiveWorkSnapshotItem[];
  hasFetchedOnce: boolean;
};

/**
 * Discovers in-flight scan-video analysis operations (`VideoToScopeJob` and
 * `VideoGroupImageCaptureJob`) for a scan via the Operations API, joined with ScanVideo metadata for
 * modal rehydration and trigger-button loaders.
 *
 * Two disciplines keep this from being an always-on, over-broad poller:
 *   - Self-terminating: it discovers once on mount/refetch and only keeps the interval alive while
 *     work it can follow is genuinely in flight; an idle scan does a single pass then goes silent.
 *   - Interaction-correlated: it adopts only operations THIS tab started (remembered per scan in
 *     sessionStorage), so a user's other in-flight analysis jobs for the same scan never rehydrate.
 */
export const useAIScanVideoActiveWork = (scanId: string, enabled = true) => {
  const kaiProApiClient = useKaiProApiClient();
  const { userId } = useAuth();
  const kaiProApiClientRef = useRef(kaiProApiClient);
  kaiProApiClientRef.current = kaiProApiClient;

  const [items, setItems] = useState<readonly AIScanVideoActiveWorkSnapshotItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Last-known "is anything still active" so a transient poll failure keeps the current cadence
  // instead of tearing down an otherwise-live poll on one error.
  const hasActiveRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  /**
   * One discovery pass. Adopts only remembered (this-tab-started) operations for the scan and
   * returns whether any adopted item is still active — the signal the interval controller uses to
   * keep or drop the poll.
   */
  const fetchActiveWork = useCallback(async (): Promise<boolean> => {
    if (!scanId) return false;
    try {
      const rememberedOperationIds = readRememberedScanVideoOperationIds(scanId);
      // Filter to this tab's own operations BEFORE picking the latest-per-video. If we picked first
      // and filtered after, a stranger's (or a stale prior-session) operation could be the survivor
      // for a videoId and then get rejected, silently discarding this tab's own live operation.
      const ownedRawOperations = (
        await listActiveScanVideoAnalysisOperationsForScan(kaiProApiClientRef.current, scanId)
      ).filter(operation =>
        rememberedOperationIds.has(operation.id)
        && (!userId || operation.userId === userId)
      );
      const ownedOperations = pickLatestScanVideoAnalysisOperationPerVideo(ownedRawOperations);

      const videoIds = ownedOperations
        .map(operation => readVideoIdFromScanVideoAnalysisInput(operation.input))
        .filter((videoId): videoId is string => !!videoId);

      if (!videoIds.length) {
        setItems([]);
        hasActiveRef.current = false;
        return false;
      }

      const submissions = await kaiProApiClientRef.current.getScanVideosById(videoIds);
      const submissionById = new Map(submissions.map(submission => [submission.id, submission]));

      const activeItems = ownedOperations.flatMap(operation => {
        const videoId = readVideoIdFromScanVideoAnalysisInput(operation.input);
        if (!videoId) return [];
        const submission = submissionById.get(videoId);
        if (!submission || submission.scanId !== scanId) return [];
        return [{
          ...toApiScanVideoActiveWorkItemFromOperation(
            {
              id: submission.id,
              originalFileName: submission.originalFileName,
              mime: submission.mime,
              bytes: submission.bytes,
            },
            operation,
          ),
          operationId: operation.id,
        }];
      });

      setItems(activeItems);
      hasActiveRef.current = activeItems.length > 0;
      return hasActiveRef.current;
    } catch {
      // Keep last-known-good items so a transient poll failure does not flicker trigger loaders,
      // and keep the previous cadence decision.
      return hasActiveRef.current;
    } finally {
      setIsLoading(false);
      setHasFetchedOnce(true);
    }
  }, [scanId, userId]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      void (async () => {
        const stillActive = await fetchActiveWork();
        if (!stillActive) stopPolling();
      })();
    }, SCAN_VIDEO_ACTIVE_WORK_POLL_INTERVAL_MS);
  }, [fetchActiveWork, stopPolling]);

  /** Discover once now and (re)start the interval only while there is active work to follow. */
  const refetch = useCallback(async () => {
    const stillActive = await fetchActiveWork();
    if (stillActive) startPolling();
    else stopPolling();
  }, [fetchActiveWork, startPolling, stopPolling]);

  useEffect(() => {
    if (!enabled || !scanId) {
      stopPolling();
      setItems([]);
      setIsLoading(false);
      setHasFetchedOnce(false);
      hasActiveRef.current = false;
      return;
    }

    let cancelled = false;

    setIsLoading(true);
    setHasFetchedOnce(false);
    void (async () => {
      const stillActive = await fetchActiveWork();
      if (cancelled) return;
      if (stillActive) startPolling();
    })();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [enabled, fetchActiveWork, scanId, startPolling, stopPolling]);

  return {
    items,
    isLoading,
    hasFetchedOnce,
    hasActiveWork: items.length > 0,
    activeCount: items.length,
    refetch,
  };
};
